import formatToAPIDate from "@/utils/formatToAPIDate";
import differenceInDays from "date-fns/differenceInDays";
import differenceInCalendarMonths from "date-fns/differenceInCalendarMonths";
import { DATA_API_SERVICE } from "@/appConfig";
import DisconnectMetrics from "@/classes/disconnectMetrics";
import { EntityType } from "@/classes/entity";
import API from "@/api/api";
import AdminReport, { AdminReportResponse } from "@/classes/adminReport";

export type ReportResult = {
  start: string;
  metricValue: number;
  end: string;
};

type Grouping = "HOUR" | "DAY" | "MONTH" | "YEAR" | "AUTO";
type IdType = EntityType | null;

export type ReportDataResponse = {
  data: ReportResult[];
};

async function getAdminReport(days: number) {
  const response = await API.get<AdminReportResponse>(`${DATA_API_SERVICE}/report/days/${days}`, {
    cache: {
      ignoreCache: true
    }
  });
  return AdminReport.fromAPI(response.data);
}

async function query(
  start: Date,
  end: Date,
  id: string,
  idType: IdType,
  grouping: Grouping = "AUTO",
  endpoint = "report"
) {
  // Since we only pass one id, we need to figure out which one
  let derivedIdType = "organization";
  switch (idType) {
    case EntityType.ORGANIZATION:
      derivedIdType = "orgUUID";
      break;
    case EntityType.PROPERTY:
      derivedIdType = "propertyUUID";
      break;
    case EntityType.STATION:
      derivedIdType = "stationUUID";
      break;
    default:
      derivedIdType = "userUUID";
  }

  // Caculate Grouping if AUTO
  let newGrouping = grouping;
  const newStart = start;
  const newEnd = end;
  const dayDiff = differenceInDays(newStart, newEnd);
  const monthDiff = differenceInCalendarMonths(newStart, newEnd);
  if (grouping === "AUTO") {
    if (Math.abs(dayDiff) <= 7) {
      newGrouping = "HOUR";
    } else if (Math.abs(monthDiff) <= 1) {
      newGrouping = "DAY";
    } else if (Math.abs(monthDiff) <= 12) {
      newGrouping = "MONTH";
    } else {
      newGrouping = "YEAR";
    }
  }

  const response = await API.post<ReportDataResponse>(
    `${DATA_API_SERVICE}/${endpoint}`,
    {
      start: formatToAPIDate(newStart),
      end: formatToAPIDate(newEnd),
      grouping: newGrouping,
      [derivedIdType]: id
    },
    {
      cache: {
        ignoreCache: true
      }
    }
  );

  return DisconnectMetrics.fromAPI(response.data);
}

const DisconnectAPIReport = {
  getAdminReport,
  query
};

export default DisconnectAPIReport;
